import { PolicyAction, PolicyFormValues } from './policy-dialog';
import SharedReviewLayout, {
  ReviewSection,
  TargetType,
} from '../../shared/shared-review-layout';
import SharedDetailAccordion from '../../shared/shared-detail-accordion';
import { policyActions } from '../../shared/policy-actions';
import { policyNarratives } from './policy-review-narratives';
import { formatPolicyScheduleTimes, transformActionHash } from './policy-utils';
import Grid from '@mui/material/Grid';

interface StepSevenProps {
  selectedPolicy: Partial<PolicyFormValues>;
  mode?: string;
  apiHost?: string;
}

export default function StepSeven({
  selectedPolicy,
  mode,
  apiHost,
}: StepSevenProps) {
  const times = selectedPolicy.times ?? [];
  const dates = selectedPolicy.dates ?? [];
  const actionData =
    mode === 'detail' && selectedPolicy.actionHash
      ? transformActionHash(selectedPolicy.actionHash)
      : selectedPolicy.actionData;

  function createDetailNarrative(action: PolicyAction) {
    const narrativeFn = policyNarratives[action.type];
    const narrative =
      typeof narrativeFn === 'function'
        ? narrativeFn(action, selectedPolicy)
        : narrativeFn;
    return narrative;
  }

  const sections: ReviewSection[] = [
    {
      title: 'Basic Information',
      leftItems: [
        { label: 'Policy Name', value: selectedPolicy.policyName },
        {
          label: 'Policy Type',
          value:
            selectedPolicy.policyType === 'standard'
              ? 'Standard'
              : 'New User Created',
        },
        {
          label: 'Description',
          value: selectedPolicy.description || 'None Provided',
        },
        {
          label: 'Manual Run',
          value: selectedPolicy.manualRun,
        },
        {
          label: 'Recurring Schedule',
          value: selectedPolicy.scheduled,
        },
        {
          label: 'Set Run Date',
          value: selectedPolicy.useRunDate,
        },
        {
          label: 'Run Date',
          value: selectedPolicy.runDate
            ? new Date(selectedPolicy.runDate).toLocaleString()
            : undefined,
          isVisible: Boolean(
            selectedPolicy.useRunDate && selectedPolicy.runDate
          ),
        },
        {
          label: 'Emails to Notify',
          value: selectedPolicy.emailsToNotify?.map((user) => ({
            email: user,
            type: 'Unknown' as const,
          })),
          variant: 'chip',
          flex: true,
          isVisible: Boolean(
            selectedPolicy.notifyViaEmail &&
              selectedPolicy.emailsToNotify &&
              selectedPolicy.emailsToNotify.length > 0
          ),
        },
      ],
      rightItems:
        mode === 'detail'
          ? [
              {
                label: 'Policy ID',
                value: selectedPolicy.id,
                isVisible: !!(selectedPolicy.id && selectedPolicy.id !== 0),
              },
              {
                label: 'Created By',
                value: [
                  { email: selectedPolicy.createdBy ?? '', type: 'User' },
                ],
                isVisible: !!(
                  selectedPolicy.createdBy && selectedPolicy.createdBy !== ''
                ),
                variant: 'chip',
              },
              {
                label: 'Created Date',
                value: selectedPolicy.createdDate,
                variant: 'date',
                isVisible: !!(
                  selectedPolicy.createdDate && selectedPolicy.createdDate !== 0
                ),
              },
              {
                label: 'Last Ran On',
                value: selectedPolicy.lastRunDate
                  ? new Date(selectedPolicy.lastRunDate).toLocaleString()
                  : 'Never Ran',
                isVisible: !!(mode === 'detail'),
              },
              {
                label: 'API Endpoint',
                value:
                  selectedPolicy.policyType === 'standard'
                    ? apiHost + '/v1/policy'
                    : apiHost + '/v1/policy/onboarding',
                isVisible: !!apiHost,
              },
            ]
          : [],
    },
    {
      title: 'Actions',
      leftItems: [
        {
          value: (
            <Grid size={{ xs: 10 }}>
              {actionData?.map((action) => (
                <SharedDetailAccordion
                  key={action.type}
                  index={action.type}
                  title={
                    policyActions.find((x) => x.value === action.type)?.label ||
                    ''
                  }
                  detail={createDetailNarrative(action)}
                />
              ))}
            </Grid>
          ),
        },
      ],
    },
    ...(selectedPolicy.policyType !== 'newUser'
      ? [
          {
            title: 'Targets',
            leftItems: [
              { label: 'Target Type', value: selectedPolicy.sourceType },
              {
                label: 'Targets',
                value: !selectedPolicy.allSource
                  ? selectedPolicy.sources?.map((user) => ({
                      email: user,
                      type: selectedPolicy.sourceType as TargetType,
                    }))
                  : 'All',
                variant: !selectedPolicy.allSource ? 'chip' : 'default',
                flex: !selectedPolicy.allSource ? true : false,
                isVisible: Boolean(
                  selectedPolicy.allSource ||
                    (selectedPolicy.sources &&
                      selectedPolicy.sources.length > 0)
                ),
              },
            ],
          },
        ]
      : []),
    ...(selectedPolicy.scheduled && selectedPolicy.policyType === 'standard'
      ? [
          {
            title: 'Recurring Schedule',
            leftItems: [
              {
                label: 'Times',
                value: formatPolicyScheduleTimes(times),
              },
              {
                label: 'Dates',
                value: dates.map((d) => d),
              },
              {
                label: 'Expiration Date',
                value: selectedPolicy.scheduleEndDate
                  ? new Date(
                      selectedPolicy.scheduleEndDate
                    ).toLocaleDateString()
                  : '',
                isVisible: !!(
                  selectedPolicy.scheduleEndDate &&
                  selectedPolicy.scheduleEndDate !== 0
                ),
              },
            ],
          },
        ]
      : []),
  ];

  return <SharedReviewLayout sections={sections} />;
}
